import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMemberLinks, useRefreshCatalog, useRemoveCheckoutLink, useSetMemberCode, useUsers } from '@/hooks/queries';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { ConfirmDialog, EmptyState, ErrorState, TableSkeleton } from '@/components/ui';
import { IconCheck, IconCopy, IconEdit, IconPlus, IconRepeat, IconSearch, IconTrash, IconUsers, IconX } from '@/components/Icons';
import { CheckoutLinkModal, type EditingCheckout } from '@/components/links/CheckoutLinkModal';
import { LinkAccessModal } from '@/components/links/LinkAccessModal';
import { MemberPicker } from '@/components/links/MemberPicker';
import { copyText } from '@/lib/clipboard';
import { formatBRL, formatMoney, formatNumber } from '@/lib/format';
import { matchesLink, parsePriceQuery, searchWords, type PriceRange } from '@/lib/linkSearch';
import type { Billing, MemberLink } from '@/lib/types';

const COPIED_MS = 1_800;

/** Agrupa pela chave, mantendo a ordem que a API já manda. */
function groupBy<T>(items: readonly T[], keyOf: (item: T) => string): [string, T[]][] {
  const groups = new Map<string, T[]>();
  for (const item of items) groups.set(keyOf(item), [...(groups.get(keyOf(item)) ?? []), item]);
  return [...groups.entries()];
}

/** Grupo (plataforma) → produto → links. */
const groupLinks = (links: readonly MemberLink[]) =>
  groupBy(links, (link) => link.group).map(([group, items]) => [group, groupBy(items, (link) => link.productName)] as const);

/** Id da aba da plataforma: só letras e números no atributo. */
const platformTabId = (group: string) => `links-tab-${group.normalize('NFD').replace(/[^A-Za-z0-9]+/g, '-').toLowerCase()}`;

/** O domínio do checkout só diz algo quando muda dentro da plataforma (na Cakto, é sempre o mesmo). */
const hostsVary = (products: readonly (readonly [string, MemberLink[]])[]) =>
  new Set(products.flatMap(([, links]) => links.map((link) => link.checkout?.host ?? ''))).size > 1;

/** As ofertas da Ephra não aparecem mais: só os links de checkout (e os da Cakto). */
const visibleLinks = (links: readonly MemberLink[]) => links.filter((link) => link.source !== 'ephra');

const count = (value: number, one: string, many: string) => `${formatNumber(value)} ${value === 1 ? one : many}`;

/** O valor procurado como foi pedido: "R$ 697" (o real inteiro) ou "R$ 4.999,97" (com os centavos). */
const priceLabel = (range: PriceRange) => (range.exact ? formatBRL(range.min) : `R$ ${formatNumber(range.min / 100)}`);

/** O modal aberto: cadastro novo, edição de um link, a conta da Kirvano recém-conectada, ou nenhum. */
type CheckoutModalState = { editing?: EditingCheckout; kirvanoAccountId?: string } | null;

/** Por que o "entrar com a Kirvano" não terminou (o motivo vem na volta). */
const KIRVANO_ERRORS: Record<string, string> = {
  expirou: 'O login da Kirvano demorou demais ou já foi usado. Tente conectar de novo.',
  negado: 'A Kirvano não autorizou o acesso. Para conectar, aceite a leitura de produtos e vendas.',
  recusado: 'A Kirvano não completou o login. Tente conectar de novo.',
  'outro-negocio': 'Você entrou com outro negócio da Kirvano. Para reconectar esta conta, entre com o mesmo negócio.',
  falhou: 'Não deu para falar com a Kirvano agora. Tente de novo em alguns minutos.',
};

export function LinksPage() {
  const { user, isAdmin } = useAuth();
  const toast = useToast();
  const [memberId, setMemberId] = useState('');
  const [checkoutModal, setCheckoutModal] = useState<CheckoutModalState>(null);
  const [removing, setRemoving] = useState<MemberLink | null>(null);
  /** A plataforma em vista (Cakto, Kirvano…): cada uma na sua aba. */
  const [platform, setPlatform] = useState('');
  const [accessOf, setAccessOf] = useState<MemberLink | null>(null);
  /** A busca: palavras do produto ou da oferta, e o valor. */
  const [search, setSearch] = useState('');
  const [priceText, setPriceText] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: members } = useUsers({}, { enabled: isAdmin });
  const { data, isLoading, isPlaceholderData, error } = useMemberLinks(memberId ? { memberId } : {});
  const refresh = useRefreshCatalog();
  const remove = useRemoveCheckoutLink();

  // A volta do "entrar com a Kirvano": avisa uma vez só e reabre o cadastro na conta.
  // A chave guardada impede que o mesmo retorno avise de novo a cada nova renderização.
  const handledReturn = useRef<string | null>(null);
  useEffect(() => {
    const status = searchParams.get('kirvano');
    const key = searchParams.toString();
    if (!status || handledReturn.current === key) return;
    handledReturn.current = key;
    if (status === 'conectada') {
      toast.success('Conta da Kirvano conectada. Os produtos estão chegando, escondidos dos membros até você liberar.');
      setCheckoutModal({ kirvanoAccountId: searchParams.get('conta') ?? undefined });
    } else {
      const detail = searchParams.get('detalhe');
      const message = KIRVANO_ERRORS[searchParams.get('motivo') ?? ''] ?? KIRVANO_ERRORS.falhou!;
      toast.error(detail ? `${message} (Kirvano: ${detail})` : message);
    }
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams, toast]);

  async function refreshCatalog() {
    try {
      const result = await refresh.mutateAsync();
      // Só as plataformas ligadas contam: cada uma relida, ou que não respondeu agora.
      const platforms = [
        { name: 'Cakto', configured: result.caktoConfigured, refreshed: result.caktoRefreshed },
        { name: 'DotPag', configured: result.dotpagConfigured, refreshed: result.dotpagRefreshed },
      ].filter((platform) => platform.configured);
      const done = platforms.filter((platform) => platform.refreshed).map((platform) => platform.name);
      const failed = platforms.filter((platform) => !platform.refreshed).map((platform) => platform.name);
      if (done.length > 0) toast.success(`Ofertas da ${done.join(' e da ')} relidas: links em dia para ${result.members} membros.`);
      if (failed.length > 0) {
        const answered = failed.length > 1 ? 'responderam' : 'respondeu';
        toast.error(`A ${failed.join(' e a ')} não ${answered} agora. Esses links continuam como estavam; tente de novo em alguns minutos.`);
      }
    } catch (caught) {
      toast.fromError(caught);
    }
  }

  async function confirmRemove() {
    if (!removing?.checkout) return;
    try {
      await remove.mutateAsync(removing.checkout.id);
      toast.success('Link removido dos links de todos os membros.');
      setRemoving(null);
    } catch (caught) {
      toast.fromError(caught);
    }
  }

  function editLink(link: MemberLink) {
    if (!link.checkout?.url) return;
    setCheckoutModal({
      editing: { id: link.checkout.id, groupName: link.group, productName: link.productName, title: link.offerTitle ?? '', url: link.checkout.url, price: link.price },
    });
  }

  if (error) return <ErrorState message={(error as Error).message} />;

  const viewingOther = Boolean(memberId && memberId !== user?.id);
  const links = visibleLinks(data?.links ?? []);
  const groups = groupLinks(links);
  const shown = groups.find(([group]) => group === platform) ?? groups[0];
  const showHost = shown ? hostsVary(shown[1]) : false;

  // A busca olha todas as plataformas de uma vez: o resultado sai em seções, uma por plataforma.
  const words = searchWords(search);
  const price = parsePriceQuery(priceText);
  const priceRange = price === 'invalid' ? null : price;
  const searching = words.length > 0 || priceRange !== null;
  const foundGroups = searching ? groupLinks(links.filter((link) => matchesLink(link, words, priceRange))) : [];
  const foundOffers = foundGroups.reduce((total, [, products]) => total + products.reduce((sum, [, items]) => sum + items.length, 0), 0);
  const foundProducts = foundGroups.reduce((total, [, products]) => total + products.length, 0);
  const criteria = [
    words.length > 0 ? `com “${search.trim()}”` : '',
    priceRange ? `de ${priceLabel(priceRange)}` : '',
  ].filter(Boolean).join(' ');

  function clearSearch() {
    setSearch('');
    setPriceText('');
  }

  /** Um produto num bloco, com as ofertas dele em linhas. */
  const productCard = ([product, items]: readonly [string, MemberLink[]], hostVaries: boolean) => (
    <section key={product} className="card link-product">
      <header className="link-product-head">
        <h3 className="link-product-name">{product}</h3>
        <span className="link-product-count num">{count(items.length, 'oferta', 'ofertas')}</span>
      </header>
      <ul className="link-offers">
        {items.map((link) => (
          <LinkRow
            key={link.id}
            link={link}
            showHost={hostVaries}
            onAccess={isAdmin && link.access ? () => setAccessOf(link) : undefined}
            onEdit={isAdmin && link.checkout?.url && !link.checkout.managed ? () => editLink(link) : undefined}
            onRemove={isAdmin && link.checkout && !link.checkout.managed ? () => setRemoving(link) : undefined}
          />
        ))}
      </ul>
    </section>
  );

  function onPlatformKey(event: KeyboardEvent<HTMLDivElement>) {
    if ((event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') || !shown) return;
    event.preventDefault();
    const index = groups.indexOf(shown);
    const next = groups[(index + (event.key === 'ArrowRight' ? 1 : -1) + groups.length) % groups.length]![0];
    setPlatform(next);
    document.getElementById(platformTabId(next))?.focus();
  }

  return (
    <>
      <h1 className="page-title">{viewingOther ? 'Links do membro' : 'Meus links'}</h1>
      <p className="page-lede">
        {viewingOther
          ? 'Um link para cada oferta, com o código deste membro. A venda paga por eles entra no nome dele, no ranking.'
          : 'Um link para cada oferta. Mande para o lead: o clique leva direto ao checkout, e a venda paga entra no seu nome, no ranking.'}
      </p>

      {isAdmin ? (
        <div className="toolbar">
          <div className="spacer" />
          {data?.caktoConfigured || data?.dotpagConfigured ? (
            <button
              type="button"
              className="btn"
              onClick={() => void refreshCatalog()}
              disabled={refresh.isPending}
              title="Produtos e ofertas novos entram sozinhos de tempos em tempos (Cakto a cada 5 minutos, DotPag a cada 30); isto busca agora."
            >
              {refresh.isPending ? <span className="spinner" /> : null} Atualizar links
            </button>
          ) : null}
          <button type="button" className="btn btn-primary" onClick={() => setCheckoutModal({})}>
            <IconPlus /> Adicionar link de checkout
          </button>
        </div>
      ) : null}

      {isLoading || !data ? (
        <TableSkeleton rows={6} columns={3} />
      ) : (
        <>
          {/* Os links do membro anterior ficam, apagados, até chegarem os do escolhido. */}
          <div className={isPlaceholderData ? 'links-body is-stale' : 'links-body'} aria-busy={isPlaceholderData}>
            <CodePanel code={data.code} baseUrl={data.baseUrl} memberId={viewingOther ? data.memberId : undefined} />
          </div>

          {isAdmin || links.length > 0 ? (
            <section className={isAdmin ? 'link-filters link-filters-admin' : 'link-filters'} aria-label="Buscar ofertas">
              {isAdmin ? (
                <>
                  <MemberPicker
                    self={user ?? null}
                    members={(members ?? []).filter((member) => member.id !== user?.id)}
                    value={memberId}
                    onChange={setMemberId}
                  />
                  <span className="link-filters-rule" aria-hidden="true" />
                </>
              ) : null}
              <label className="field">
                <span className="field-label">Produto ou oferta</span>
                <span className="search-input">
                  <span className="search-input-icon" aria-hidden="true"><IconSearch /></span>
                  <input
                    className="inp"
                    type="search"
                    autoComplete="off"
                    spellCheck={false}
                    placeholder="Ex.: ATMOZ PRO, anual, downsell"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                </span>
              </label>
              <label className="field">
                <span className="field-label">Valor</span>
                <span className="money-search">
                  <span className="money-search-prefix" aria-hidden="true">R$</span>
                  <input
                    className="inp num"
                    inputMode="decimal"
                    autoComplete="off"
                    placeholder="Ex.: 697"
                    value={priceText}
                    aria-invalid={price === 'invalid'}
                    onChange={(event) => setPriceText(event.target.value)}
                  />
                </span>
                {price === 'invalid' ? <span className="field-error">Só números, como 697.</span> : null}
              </label>
            </section>
          ) : null}

          <div className={isPlaceholderData ? 'links-body is-stale' : 'links-body'} aria-busy={isPlaceholderData}>
          {links.length === 0 ? (
            <EmptyState
              message={
                viewingOther
                  ? 'Nenhum link liberado para este membro.'
                  : isAdmin
                    ? 'Nenhum link ainda. Adicione um link de checkout.'
                    : 'Nenhum link ainda. O administrador cadastra os links de checkout.'
              }
            />
          ) : searching ? (
            <>
              <div className="link-results-head">
                <p className="link-results-count" role="status">
                  {foundOffers === 0 ? (
                    <>Nenhuma oferta {criteria}.</>
                  ) : (
                    <>
                      <strong className="num">{count(foundOffers, 'oferta', 'ofertas')}</strong>
                      {' em '}
                      <span className="num">{count(foundProducts, 'produto', 'produtos')}</span> {criteria}
                    </>
                  )}
                </p>
                <button type="button" className="btn btn-ghost btn-sm" onClick={clearSearch}>
                  <IconX /> Limpar busca
                </button>
              </div>
              {foundOffers === 0 ? (
                <p className="link-results-empty">
                  {viewingOther
                    ? 'A busca olha só os links liberados para este membro. Confira a grafia, ou tire o valor ou uma das palavras.'
                    : 'Confira a grafia, ou tire o valor ou uma das palavras.'}
                </p>
              ) : null}
              {foundGroups.map(([group, products]) => {
                const headingId = `${platformTabId(group)}-resultado`;
                const offers = products.reduce((total, [, items]) => total + items.length, 0);
                return (
                  <section key={group} className="link-products" aria-labelledby={headingId}>
                    <div className="section-head link-section-head">
                      <h2 id={headingId} className="link-section-title">
                        {group}
                        <span className="muted num">
                          {count(offers, 'oferta', 'ofertas')} · {count(products.length, 'produto', 'produtos')}
                        </span>
                      </h2>
                      <span className="section-rule" aria-hidden="true" />
                    </div>
                    {products.map((entry) => productCard(entry, hostsVary(products)))}
                  </section>
                );
              })}
            </>
          ) : (
            <>
            {groups.length > 1 ? (
              <div className="tabs" role="tablist" aria-label="Plataforma" onKeyDown={onPlatformKey} style={{ marginBottom: 22 }}>
                {groups.map(([group, products]) => (
                  <button
                    key={group}
                    type="button"
                    role="tab"
                    id={platformTabId(group)}
                    aria-selected={shown?.[0] === group}
                    aria-controls="links-panel"
                    tabIndex={shown?.[0] === group ? 0 : -1}
                    className="tab"
                    onClick={() => setPlatform(group)}
                  >
                    {group}{' '}
                    <span className="muted num" style={{ fontSize: 12 }}>
                      {products.reduce((total, [, links]) => total + links.length, 0)}
                    </span>
                  </button>
                ))}
              </div>
            ) : null}
            {(shown ? [shown] : []).map(([group, products]) => (
              <section
                key={group}
                id="links-panel"
                role={groups.length > 1 ? 'tabpanel' : undefined}
                aria-labelledby={groups.length > 1 ? platformTabId(group) : undefined}
                className="link-products"
              >
                {/* Com abas, a aba já diz a plataforma: o título fica só para o leitor de tela. */}
                <h2 className={groups.length > 1 ? 'sr-only' : 'link-group-title'}>
                  {group}
                  <span className="muted num">
                    {' '}{products.length} {products.length === 1 ? 'produto' : 'produtos'}
                  </span>
                </h2>
                {products.map((entry) => productCard(entry, showHost))}
              </section>
            ))}
            </>
          )}
          </div>
        </>
      )}

      {accessOf ? <LinkAccessModal link={accessOf} onClose={() => setAccessOf(null)} /> : null}
      {checkoutModal ? (
        <CheckoutLinkModal editing={checkoutModal.editing} kirvanoAccountId={checkoutModal.kirvanoAccountId} onClose={() => setCheckoutModal(null)} />
      ) : null}
      {removing ? (
        <ConfirmDialog
          title="Remover link de checkout"
          message={
            <>
              <strong>{removing.label}</strong> sai dos links de todos os membros, e os endereços já enviados deixam de abrir.
            </>
          }
          confirmLabel="Remover para todos"
          danger
          busy={remove.isPending}
          onConfirm={() => void confirmRemove()}
          onCancel={() => setRemoving(null)}
        />
      ) : null}
    </>
  );
}

function CodePanel({ code, baseUrl, memberId }: { code: string | null; baseUrl: string; memberId?: string }) {
  const toast = useToast();
  const setCode = useSetMemberCode();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const host = baseUrl.replace(/^https?:\/\//, '');

  async function submit(event: FormEvent) {
    event.preventDefault();
    try {
      const result = await setCode.mutateAsync({ code: draft, ...(memberId ? { memberId } : {}) });
      toast.success(`Código trocado para "${result.code}". Os links antigos continuam funcionando.`);
      setEditing(false);
    } catch (caught) {
      toast.fromError(caught);
    }
  }

  const preview = (draft.trim().toLowerCase() || code || 'seu-codigo');

  return (
    <section className="card" style={{ marginBottom: 30, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '14px 28px' }}>
      <div style={{ minWidth: 0 }}>
        <div className="label muted" style={{ fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          {memberId ? 'Código do membro' : 'Seu código'}
        </div>
        <div className="display num" style={{ fontSize: 26, fontWeight: 500, letterSpacing: '-0.02em', marginTop: 4 }}>
          {code ?? '—'}
        </div>
        <div className="muted truncate" style={{ fontSize: 12, marginTop: 4 }}>
          {host}/v/<strong style={{ color: 'var(--foreground)' }}>{editing ? preview : code ?? '…'}</strong>/…
        </div>
      </div>

      <div className="spacer" />

      {editing ? (
        <form onSubmit={(event) => void submit(event)} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <input
            className="inp"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="ex.: camila-vendas"
            aria-label="Novo código"
            autoFocus
            maxLength={24}
            style={{ width: 200 }}
          />
          <button type="submit" className="btn btn-sm btn-primary" disabled={!draft.trim() || setCode.isPending}>
            {setCode.isPending ? <span className="spinner" /> : null} Salvar
          </button>
          <button type="button" className="btn btn-sm" onClick={() => setEditing(false)}>Cancelar</button>
          <span className="muted" style={{ flexBasis: '100%', fontSize: 11.5 }}>
            3 a 24 letras minúsculas e números, separados por hífen. Os links já enviados continuam funcionando.
          </span>
        </form>
      ) : (
        <button type="button" className="btn btn-sm" onClick={() => { setDraft(code ?? ''); setEditing(true); }} disabled={!code}>
          Trocar código
        </button>
      )}
    </section>
  );
}

/** O período da assinatura, depois do preço: "R$ 697,00/mês". */
const PERIODS: Partial<Record<Billing, string>> = {
  WEEKLY: 'semana',
  MONTHLY: 'mês',
  QUARTERLY: 'trimestre',
  SEMIANNUAL: 'semestre',
  YEARLY: 'ano',
};

/** Pagamento único ou assinatura, como a plataforma do checkout diz. Sem a informação, nada. */
function BillingTag({ billing }: { billing: Billing | null }) {
  if (!billing) return null;
  if (billing === 'ONCE') {
    return <span className="chip" title="Cobra uma vez só">Pagamento único</span>;
  }
  const period = PERIODS[billing];
  return (
    <span className="chip chip-billing" title={period ? `Cobra de novo a cada ${period}` : 'Cobra de novo a cada período'}>
      <IconRepeat size={12} /> Assinatura
    </span>
  );
}

interface LinkRowProps {
  link: MemberLink;
  /** O domínio do checkout, quando ele muda dentro da plataforma. */
  showHost: boolean;
  onAccess?: () => void;
  onEdit?: () => void;
  onRemove?: () => void;
}

function LinkRow({ link, showHost, onAccess, onEdit, onRemove }: LinkRowProps) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const toast = useToast();

  useEffect(() => () => window.clearTimeout(timer.current), []);

  async function copy() {
    if (!(await copyText(link.url))) {
      toast.error('Não deu para copiar. Selecione o endereço e copie à mão.');
      return;
    }
    setCopied(true);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setCopied(false), COPIED_MS);
  }

  const period = link.billing ? PERIODS[link.billing] : undefined;

  return (
    <li className="link-offer">
      <div className="link-offer-main">
        <div className="link-offer-head">
          {link.price !== null ? (
            <span className="link-offer-price num">
              {formatMoney(link.price, link.currency)}
              {period ? (
                <span className="link-offer-period">
                  <span aria-hidden="true">/{period}</span>
                  <span className="sr-only"> por {period}</span>
                </span>
              ) : null}
            </span>
          ) : null}
          <BillingTag billing={link.billing} />
          {/* Sem preço fixo (a oferta em dólar), a descrição é o que identifica a oferta. */}
          {link.offerTitle ? (
            <span className={link.price === null ? 'link-offer-price' : 'link-offer-title'}>{link.offerTitle}</span>
          ) : null}
          {link.isDefault ? <span className="chip">padrão</span> : null}
          {showHost && link.checkout ? (
            <span className="chip" title={`O checkout fica em ${link.checkout.host}`}>{link.checkout.host}</span>
          ) : null}
        </div>
        <div className="link-offer-url truncate" title={link.url}>{link.url}</div>
      </div>

      <div className="link-offer-stats muted num" title="Cliques de gente; robôs de pré-visualização não contam">
        {formatNumber(link.clicks)} {link.clicks === 1 ? 'clique' : 'cliques'} · {formatNumber(link.uniqueClicks)} únicos
        {link.sales > 0 ? (
          <span className="link-offer-sales" title="Vendas pagas que vieram por este link">
            {' '}· {formatNumber(link.sales)} {link.sales === 1 ? 'venda' : 'vendas'} ({formatMoney(link.salesAmount, link.currency)})
          </span>
        ) : null}
      </div>

      <div className="link-offer-actions">
        <button type="button" className="btn btn-sm" onClick={() => void copy()} aria-label={`Copiar o link de ${link.label}`}>
          {copied ? <IconCheck /> : <IconCopy />} {copied ? 'Copiado' : 'Copiar'}
        </button>
        {onAccess && link.access ? (
          <button
            type="button"
            className={link.access.restricted ? 'btn btn-sm chip-primary' : 'btn btn-sm'}
            onClick={onAccess}
            aria-label={`Escolher quem recebe ${link.label}`}
            title="Escolher os membros que recebem esta oferta"
          >
            <IconUsers />{' '}
            {!link.access.restricted
              ? 'Todos'
              : link.access.members === 0
                ? 'Bloqueada'
                : `${link.access.members} ${link.access.members === 1 ? 'membro' : 'membros'}`}
          </button>
        ) : null}
        {onEdit ? (
          <button type="button" className="icon-btn" onClick={onEdit} aria-label={`Editar o link de checkout ${link.label}`} title="Editar">
            <IconEdit />
          </button>
        ) : null}
        {onRemove ? (
          <button type="button" className="icon-btn" onClick={onRemove} aria-label={`Remover o link de checkout ${link.label}`} title="Remover">
            <IconTrash />
          </button>
        ) : null}
      </div>
    </li>
  );
}
